import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faSun, faMoon, faMobileAlt, faKey, faCheckCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import {
  faInstagram, faTwitter, faFacebook, faYoutube,
  faTiktok, faTelegram, faSpotify, faDiscord, faSoundcloud,
} from '@fortawesome/free-brands-svg-icons';
import { useAuth } from '../AuthContext';
import { useTheme } from '../ThemeContext';
import CountUp from './CountUp';
import './AuthLanding.css';

const API = process.env.REACT_APP_API_URL || 'https://api.bustisha.com/api';
const icons = [faInstagram, faTwitter, faFacebook, faYoutube, faTelegram, faSpotify, faDiscord, faSoundcloud];

export default function Login() {
  const { login } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  // Mode: 'login' | 'forgot_phone' | 'forgot_reset'
  const [mode, setMode] = useState('login');

  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);

  // Forgot password form state
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Status & Feedback
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [infoMsg, setInfoMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const clearMessages = () => {
    setError('');
    setSuccessMsg('');
    setInfoMsg('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      const data = await login(email, password);
      navigate(data.user?.role === 'admin' ? '/beast' : '/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendResetOtp = async (e) => {
    e.preventDefault();
    clearMessages();
    if (!phone.trim()) {
      setError('Please enter your registered phone number.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/forgot-password/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to send reset code.');
      }
      setInfoMsg(`Verification code sent to ${phone}. (Limit: 1 code per day)`);
      setMode('forgot_reset');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    clearMessages();

    if (otp.length !== 5) {
      setError('Please enter the 5-digit verification code.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/forgot-password/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone,
          otp,
          password: newPassword,
          password_confirmation: confirmPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to reset password.');
      }

      setSuccessMsg('Password reset successfully! You can now sign in.');
      setMode('login');
      setPassword('');
      setOtp('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-landing">
      <div className="auth-floating-icons">
        {icons.map((icon, i) => (
          <FontAwesomeIcon key={i} icon={icon} className="floating-icon" />
        ))}
      </div>

      <nav className="auth-nav">
        <div className="auth-nav-inner">
          <div className="auth-nav-logo">
            <img src="/Black and Yellow Online Store Logo.png" alt="bustisha" />
            <span>bustisha</span>
          </div>
          <ul className="auth-nav-links">
            <li><Link to="/login">Sign in</Link></li>
            <li><Link to="/register">Sign up</Link></li>
            <li>
              <button className="auth-theme-btn" onClick={toggle}>
                <FontAwesomeIcon icon={theme === 'light' ? faMoon : faSun} />
              </button>
            </li>
          </ul>
        </div>
      </nav>

      <section className="auth-hero">
        <div className="auth-hero-left fade-in-left">
          <h1>Boost Your Social <br />Media Presence</h1>
          <h4>with <span>bustisha</span> SMM Panel</h4>
          <p>
            Our SMM panel helps you increase your social media presence and reach.
            Easy to use, fast delivery, and affordable prices for everyone.
          </p>
        </div>

        <div className="auth-form-card fade-in-right">
          <div className="auth-form-card-header">
            <img src="/Black and Yellow Online Store Logo.png" alt="bustisha" />
            <h3>
              {mode === 'login' && 'Sign in to Your Account'}
              {mode === 'forgot_phone' && 'Reset Your Password'}
              {mode === 'forgot_reset' && 'Set New Password'}
            </h3>
          </div>

          {error && <div className="auth-error">{error}</div>}
          {successMsg && <div className="auth-success-msg">{successMsg}</div>}
          {infoMsg && <div className="auth-info-msg">{infoMsg}</div>}

          {/* ── MODE 1: Standard Login Form ── */}
          {mode === 'login' && (
            <form onSubmit={handleLogin}>
              <div className="auth-field">
                <input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="auth-field auth-pw-wrap">
                <input
                  type={showPw ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="auth-pw-toggle"
                  onClick={() => setShowPw(!showPw)}
                  title={showPw ? 'Hide password' : 'Show password'}
                >
                  <FontAwesomeIcon icon={showPw ? faEyeSlash : faEye} />
                </button>
              </div>

              <div className="auth-forgot-row">
                <button
                  type="button"
                  className="auth-forgot-link"
                  onClick={() => {
                    clearMessages();
                    setMode('forgot_phone');
                  }}
                >
                  Forgot password?
                </button>
              </div>

              <button type="submit" className="auth-btn" disabled={loading}>
                {loading ? <><FontAwesomeIcon icon={faSpinner} spin /> Signing in...</> : 'Sign in'}
              </button>
            </form>
          )}

          {/* ── MODE 2: Forgot Password - Enter Registered Phone ── */}
          {mode === 'forgot_phone' && (
            <form onSubmit={handleSendResetOtp}>
              <p className="auth-step-desc">
                Enter your registered phone number. We will send a 5-digit verification code.
              </p>
              <div className="auth-field auth-input-icon-wrap">
                <FontAwesomeIcon icon={faMobileAlt} className="auth-field-inner-icon" />
                <input
                  type="tel"
                  placeholder="Registered phone e.g. 255712345678"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="auth-limit-note">
                ℹ️ Code request limit: 1 code per day (24 hours).
              </div>
              <button type="submit" className="auth-btn" disabled={loading}>
                {loading ? <><FontAwesomeIcon icon={faSpinner} spin /> Checking &amp; sending code...</> : 'Send Reset Code'}
              </button>
              <div className="auth-switch-view">
                <button
                  type="button"
                  className="auth-back-link"
                  onClick={() => {
                    clearMessages();
                    setMode('login');
                  }}
                >
                  ← Back to Sign in
                </button>
              </div>
            </form>
          )}

          {/* ── MODE 3: Forgot Password - Enter OTP & New Password ── */}
          {mode === 'forgot_reset' && (
            <form onSubmit={handleResetPassword}>
              <p className="auth-step-desc">
                Enter the 5-digit code sent to <strong>{phone}</strong> and choose your new password.
              </p>
              <div className="auth-field auth-input-icon-wrap">
                <FontAwesomeIcon icon={faKey} className="auth-field-inner-icon" />
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  placeholder="5-digit code (e.g. 12345)"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  required
                  autoFocus
                />
              </div>
              <div className="auth-field auth-pw-wrap">
                <input
                  type={showPw ? 'text' : 'password'}
                  placeholder="New password (min 6 chars)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  className="auth-pw-toggle"
                  onClick={() => setShowPw(!showPw)}
                  title={showPw ? 'Hide password' : 'Show password'}
                >
                  <FontAwesomeIcon icon={showPw ? faEyeSlash : faEye} />
                </button>
              </div>
              <div className="auth-field">
                <input
                  type={showPw ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              <button type="submit" className="auth-btn" disabled={loading}>
                {loading ? <><FontAwesomeIcon icon={faSpinner} spin /> Resetting password...</> : <><FontAwesomeIcon icon={faCheckCircle} /> Save New Password</>}
              </button>
              <div className="auth-switch-view">
                <button
                  type="button"
                  className="auth-back-link"
                  onClick={() => {
                    clearMessages();
                    setMode('forgot_phone');
                  }}
                >
                  ← Change phone number
                </button>
              </div>
            </form>
          )}

          <p className="auth-form-footer">
            Don't have an account? <Link to="/register">Sign up</Link>
          </p>
        </div>
      </section>


      <section className="auth-services fade-in-up">
        <div className="auth-services-inner">
          <h2 className="auth-section-title">Top SMM Services</h2>
          <p className="auth-section-sub">We provide high-quality services for all major platforms</p>
          <div className="auth-platforms-grid">
            {[
              { name: 'Instagram', icon: faInstagram, color: '#E4405F', services: 5, from: '500' },
              { name: 'YouTube', icon: faYoutube, color: '#FF0000', services: 3, from: '500' },
              { name: 'TikTok', icon: faTiktok, color: '#000000', services: 3, from: '300' },
              { name: 'Twitter', icon: faTwitter, color: '#1DA1F2', services: 2, from: '600' },
              { name: 'Telegram', icon: faTelegram, color: '#26A5E4', services: 1, from: '4,000' },
              { name: 'Facebook', icon: faFacebook, color: '#1877F2', services: 1, from: '3,000' },
              { name: 'Spotify', icon: faSpotify, color: '#1DB954', services: 1, from: '1,500' },
              { name: 'Discord', icon: faDiscord, color: '#5865F2', services: 2, from: '6,000' },
            ].map((p) => (
              <div key={p.name} className="auth-platform-card" style={{ '--plat-color': p.color }}>
                <div className="auth-platform-icon" style={{ background: `${p.color}15` }}>
                  <FontAwesomeIcon icon={p.icon} style={{ color: p.color }} />
                </div>
                <h4>{p.name}</h4>
                <p className="auth-platform-services">{p.services} services</p>
                <p className="auth-platform-from">From TSHS {p.from}/1K</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="auth-stats fade-in-up">
        <div className="auth-stats-grid">
          {[
            { label: 'Total Orders', value: '32245386' },
            { label: 'Starting Price', value: null, text: '$0.001' },
            { label: 'Fastest Support', value: null, text: '24/7' },
            { label: 'Happy Clients', value: '14509', suffix: '+' },
          ].map((s) => (
            <div key={s.label} className="auth-stat-item">
              <h4>{s.value != null ? <CountUp end={s.value} suffix={s.suffix || ''} /> : s.text}</h4>
              <p>{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="auth-footer">
        &copy; 2026 bustisha | All Rights Reserved
        <span className="auth-footer-links">
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
        </span>
      </footer>
    </div>
  );
}
